import React from 'react';
import {useSelector} from 'react-redux';
import {PieChart, Pie, Cell} from 'recharts';

import IconMenuVisits from 'assets/icon-menu-visits.svg';
import IconCart from 'assets/icon-cart.svg';
import {formatTrendPercent} from 'routes/App/MainPage/Components/GrowthPrograms/utils';
import {colorForRank, formatCount, formatPercent, OTHER_COLOR} from './utils';
import {
  CardBase,
  CardHeader,
  CardsRow,
  DonutCenter,
  DonutContainer,
  DonutLegend,
  DonutWrap,
  KpiCaption,
  KpiCard,
  KpiLabel,
  KpiRow,
  KpiValueRow,
  LegendItem,
  SectionHeader,
  SectionSubtitle,
  SourceCell,
  SourceTable
} from './styles';

const LAST_4_WEEKS_LABEL = 'Last 4 weeks';

const TrafficBreakdown = () => {
  const trafficBreakdown = useSelector(
    (state) => state.mainPageReducer?.analyticsInfo?.trafficBreakdown
  );

  if (!trafficBreakdown || trafficBreakdown.totalVisits === 0) {
    return null;
  }

  const {
    menuVisits,
    cartToPurchase,
    totalVisits,
    sources,
    other
  } = trafficBreakdown;

  const menuVisitsTrend = formatTrendPercent(menuVisits.deltaPercent);
  const cartToPurchaseTrend = formatTrendPercent(cartToPurchase.deltaPercent);

  const donutData = [
    ...sources.map((s) => ({
      name: s.category,
      value: s.percentOfTotal
    })),
    {name: other.category, value: other.percentOfTotal}
  ];

  return (
    <>
      <SectionHeader>
        <h2>Traffic Breakdown</h2>
        <span className='divider' />
      </SectionHeader>
      <SectionSubtitle>
        Sauce is actively optimizing all your traffic sources — bringing more
        visitors and higher-quality traffic to boost your orders.
      </SectionSubtitle>

      <KpiRow>
        <KpiCard>
          <KpiLabel>
            <img src={IconMenuVisits} alt='' className='icon' />
            Menu Visits
          </KpiLabel>
          <KpiValueRow>
            <span className='value'>{formatCount(menuVisits?.value)}</span>
            {menuVisitsTrend && (
              <span className={`trend${menuVisitsTrend.className}`}>
                {menuVisitsTrend.label}
              </span>
            )}
          </KpiValueRow>
          <KpiCaption>{LAST_4_WEEKS_LABEL}</KpiCaption>
        </KpiCard>

        <KpiCard>
          <KpiLabel>
            <img src={IconCart} alt='' className='icon' />
            Cart-to-Purchase
          </KpiLabel>
          <KpiValueRow>
            <span className='value'>
              {formatPercent(cartToPurchase?.value)}
            </span>
            {cartToPurchaseTrend && (
              <span className={`trend${cartToPurchaseTrend.className}`}>
                {cartToPurchaseTrend.label}
              </span>
            )}
          </KpiValueRow>
          <KpiCaption>{LAST_4_WEEKS_LABEL}</KpiCaption>
        </KpiCard>
      </KpiRow>

      <CardsRow>
        <CardBase>
          <CardHeader>
            <div className='title'>Your Traffic Sources</div>
            <div className='caption'>{LAST_4_WEEKS_LABEL}</div>
          </CardHeader>
          <SourceTable>
            <thead>
              <tr>
                <th>Source</th>
                <th>Visits</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source, index) => {
                const color = colorForRank(index);
                return (
                  <tr key={source.category}>
                    <td>
                      <SourceCell>
                        <div className='name'>
                          <span className='rank'>{index + 1}</span>
                          {source.category}
                        </div>
                        <div
                          className='bar'
                          style={{
                            width: `${source.percentOfTotal}%`,
                            background: color,
                            opacity: 0.2
                          }}
                        />
                      </SourceCell>
                    </td>
                    <td>{formatCount(source.visits)}</td>
                    <td>{formatCount(source.orders)}</td>
                  </tr>
                );
              })}
            </tbody>
          </SourceTable>
        </CardBase>

        <CardBase>
          <CardHeader>
            <div className='title'>Traffic Source Mix</div>
            <div className='caption'>{LAST_4_WEEKS_LABEL}</div>
          </CardHeader>
          <DonutWrap>
            <DonutContainer>
              <PieChart
                width={180}
                height={180}
                margin={{top: 0, right: 0, bottom: 0, left: 0}}
              >
                <Pie
                  data={donutData}
                  cx='50%'
                  cy='50%'
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={0}
                  dataKey='value'
                  stroke='none'
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {donutData.map((entry, i) => (
                    <Cell
                      key={entry.name}
                      fill={
                        i === donutData.length - 1
                          ? OTHER_COLOR
                          : colorForRank(i)
                      }
                    />
                  ))}
                </Pie>
              </PieChart>
              <DonutCenter>
                <div className='big'>{formatCount(totalVisits)}</div>
                <div className='small'>total visits</div>
              </DonutCenter>
            </DonutContainer>
            <DonutLegend>
              {donutData.map((entry, i) => (
                <LegendItem key={entry.name}>
                  <span
                    className='dot'
                    style={{
                      background:
                        i === donutData.length - 1
                          ? OTHER_COLOR
                          : colorForRank(i)
                    }}
                  />
                  {entry.name}
                  <span className='pct'>{formatPercent(entry.value)}</span>
                </LegendItem>
              ))}
            </DonutLegend>
          </DonutWrap>
        </CardBase>
      </CardsRow>
    </>
  );
};

export default TrafficBreakdown;
